import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Calendar, User, DollarSign, CheckCircle, Clock, AlertCircle, FileText } from 'lucide-react';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUtils';

const Units = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [units, setUnits] = useState([]);
  const [courses, setCourses] = useState([]); // Added to store course objects
  const [selectedCourse, setSelectedCourse] = useState(state?.fromCourse || null); // For self-paced drill-down
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(state?.fromFilter || 'all');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching units for student ID:', user.id);

      // First, get student details to get courseId and campusId
      const studentResponse = await axios.get(`/students/${user.id}?includeCourse=true&includeCampus=true`);
      const studentData = studentResponse.data;
      setStudentData(studentData);
      console.log('✅ Student data loaded:', studentData);

      // Fetch available courses from API
      console.log('🌐 Fetching available courses from API...');
      const availableCoursesResponse = await axios.get(`/enrollments/available-courses?studentId=${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('✅ Available courses API response:', availableCoursesResponse.data);
      const availableCoursesData = availableCoursesResponse.data || [];
      setCourses(availableCoursesData);

      // Handle self-paced vs instructor-led logic
      const isSelfPaced = studentData.institution?.learningMode === 'self_paced' ||
        availableCoursesData.some(c => c.selfPacedEnabled);

      if (!studentData.courseId && !isSelfPaced) {
        console.log('❌ Student has no courseId and is not self-paced');
        setUnits([]);
        return;
      }

      // Flatten all units from all available courses for initial state/stats
      // But we'll filter them by selectedCourse if in self-paced mode
      const courseUnitsData = [];
      availableCoursesData.forEach((course) => {
        if (course.Units) {
          courseUnitsData.push(...course.Units.map(unit => ({
            ...unit,
            courseName: course.name,
            courseId: course.id,
            selfPacedEnabled: course.selfPacedEnabled || studentData.institution?.learningMode === 'self_paced'
          })));
        }
      });

      console.log(`📋 Total flattened units: ${courseUnitsData.length}`);

      // Fetch cost and enrollment info (Keep existing logic)
      // ... (merged logic simplified below for brevity in replace)

      // Get student's enrolled units
      const studentUnitsResponse = await axios.get('/student-units');
      const studentUnitsData = studentUnitsResponse.data || [];
      const studentUnitMap = new Map(studentUnitsData.map(su => [su.unitId, su]));

      const processedUnits = courseUnitsData.map((unit) => {
        const studentUnit = studentUnitMap.get(unit.id);

        return {
          ...unit,
          cost: unit.cost || null,
          currency: unit.currency || 'KES',
          isFree: unit.isFree ?? studentUnit?.unit?.isFree ?? false,
          freeDescription: unit.freeDescription || studentUnit?.unit?.freeDescription || null,
          originalStatus: unit.status,
          enrollmentStatus: studentUnit ? studentUnit.status : 'not_enrolled',
          paymentStatus: studentUnit ? studentUnit.paymentStatus : 'not_enrolled',
          studentUnitId: studentUnit ? studentUnit.id : null,
          progress: studentUnit ? calculateProgress(studentUnit.status) : 0,
          hasLiveLesson: studentUnit?.hasLiveLesson || false,
          liveLessonTitle: studentUnit?.liveLessonTitle || null,
          hasActiveVirtualClass: studentUnit?.hasActiveVirtualClass || false,
          currentCohortStatus: studentUnit?.unit?.currentCohortStatus,
          minClassSize: studentUnit?.unit?.minClassSize,
          enrolledCount: studentUnit?.unit?.enrolledCount,
          cohortProgress: studentUnit?.unit?.cohortProgress,
          Lecturer: studentUnit?.AssignedTutor || studentUnit?.unit?.lecturer || unit.Lecturer || unit.lecturer
        };
      });

      setUnits(processedUnits);
    } catch (error) {
      console.error('Error fetching units:', error);
      setError('Failed to load units. Please try again.');
      setUnits([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (status) => {
    switch (status) {
      case 'completed': return 100;
      case 'in-progress': return 75;
      case 'active': return 50;
      default: return 0;
    }
  };

  const handleEnrollUnit = async (unitId, unitName) => {
    try {
      const response = await axios.post('/enrollments/enroll-unit', {
        studentId: user.id,
        unitId
      });

      if (response.data) {
        const enrollment = response.data.enrollment;
        if (enrollment && enrollment.paymentStatus === 'confirmed') {
          // Free unit auto-confirmed
          alert(`Successfully enrolled in ${unitName}. This is a free course!`);
          // Refresh list or navigate to unit
          fetchUnits();
        } else {
          // Enrollment successful - navigate to payment
          alert(`Successfully enrolled in ${unitName}. Proceeding to payment...`);
          navigate(`/payment/${unitId}`);
        }
      } else {
        alert('Failed to enroll in unit');
      }
    } catch (error) {
      console.error('Error enrolling in unit:', error);
      alert('Failed to enroll in unit. Please try again.');
    }
  };

  const handleDownloadTranscript = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(`/students/${user.id}/transcript`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transcript_${user.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading transcript:', error);
      alert('Failed to download transcript. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadUnitTranscript = async (unitId, unitName) => {
    try {
      setDownloading(true);
      const response = await axios.get(`/student-units/${unitId}/unit-transcript`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transcript_${unitName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading unit transcript:', error);
      alert('Failed to download unit transcript. Please try again.');
    } finally {
      setDownloading(false);
    }
  };
  const handleRetakeUnit = async (studentUnitId, unitId) => {
    try {
      setLoading(true);
      await axios.post(`/student-units/${user.id}/${unitId}/retake`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      alert('Unit reset successfully! You can now enroll again.');
      fetchUnits(); // Refresh the list
    } catch (error) {
      console.error('Error resetting unit:', error);
      alert(error.response?.data?.message || 'Failed to reset unit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = units.filter(unit => {
    // If a course is selected (drill-down), only show units for that course
    if (selectedCourse && unit.courseId !== selectedCourse.id) return false;

    if (filter === 'all') return true;
    if (filter === 'active') return unit.status === 'active';
    if (filter === 'completed') return unit.status === 'completed';
    if (filter === 'upcoming') return unit.status === 'upcoming';
    if (filter === 'pending') return unit.paymentStatus === 'pending';
    return true;
  });

  const isSelfPacedMode = studentData?.institution?.learningMode === 'self_paced' || studentData?.course?.selfPacedEnabled;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'upcoming': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'active': return 'Active';
      case 'upcoming': return 'Upcoming';
      default: return 'Pending';
    }
  };

  const UnitCard = ({ unit }) => {
    // Determine display status based on enrollment and payment
    let displayStatus = unit.originalStatus;
    let statusColor = getStatusColor(displayStatus);
    let statusText = getStatusText(displayStatus);
    let cardOpacity = 'opacity-100';
    let cardBg = 'bg-white';

    if (unit.enrollmentStatus === 'not_enrolled') {
      // Show "Upcoming" for unenrolled units that are active/upcoming
      if (unit.originalStatus === 'upcoming' || unit.originalStatus === 'active') {
        statusText = 'Available to Enroll';
        statusColor = 'text-blue-600 bg-blue-100';
        cardBg = 'bg-blue-50/30';
      } else if (unit.originalStatus === 'pending') {
        statusText = 'Coming Soon';
        statusColor = 'text-gray-600 bg-gray-100';
        cardOpacity = 'opacity-60';
      } else {
        statusText = 'Not Available';
        statusColor = 'text-gray-600 bg-gray-100';
        cardOpacity = 'opacity-60';
      }
    } else if (unit.paymentStatus === 'pending' && !unit.isFree) {
      statusText = 'Payment Required';
      statusColor = 'text-orange-600 bg-orange-100';
      cardBg = 'bg-orange-50/30';
    } else if (unit.isFree && unit.enrollmentStatus !== 'not_enrolled') {
      statusText = 'Enrolled — Free';
      statusColor = 'text-indigo-600 bg-indigo-100';
      cardBg = 'bg-indigo-50/20';
    } else if (unit.paymentStatus === 'confirmed') {
      statusText = 'Enrolled';
      statusColor = 'text-green-600 bg-green-100';
      cardBg = 'bg-green-50/30';
    }

    // Get appropriate icon based on unit status
    const getStatusIcon = () => {
      if (unit.enrollmentStatus === 'not_enrolled') {
        if (unit.originalStatus === 'upcoming' || unit.originalStatus === 'active') {
          return '📚'; // Available to enroll
        }
        return '⏳'; // Coming soon or not available
      } else if (unit.isFree && unit.enrollmentStatus !== 'not_enrolled') {
        return '🎁'; // Free course
      } else if (unit.paymentStatus === 'pending') {
        return '💳'; // Payment required
      } else if (unit.enrollmentStatus === 'active') {
        return '🎯'; // Active learning
      } else if (unit.enrollmentStatus === 'completed') {
        return '🏆'; // Completed
      }
      return '📖'; // Default
    };

    const handleCardClick = () => {
      // For self-paced units, ALWAYS navigate to the self-paced learning view
      if (unit.selfPacedEnabled) {
        navigate(`/self-paced/unit/${unit.id}`, { 
          state: { fromCourse: selectedCourse, fromFilter: filter } 
        });
        return;
      }

      // Prevent navigation if completed (non-self-paced)
      if (unit.enrollmentStatus === 'completed') return;

      // Block navigation if payment is still required (non-free unit)
      if (unit.enrollmentStatus !== 'not_enrolled' && unit.paymentStatus === 'pending' && !unit.isFree) return;

      navigate(`/units/${unit.id}`, {
        state: { unit, fromCourse: selectedCourse, fromFilter: filter }
      });
    };

    return (
      <div
        className={`card hover:shadow-large transition-all duration-300 ${unit.enrollmentStatus === 'completed' ? 'cursor-default' : 'cursor-pointer'} ${cardOpacity} ${cardBg} border-l-4 ${unit.enrollmentStatus === 'not_enrolled' && (unit.originalStatus === 'upcoming' || unit.originalStatus === 'active')
          ? 'border-l-blue-500'
          : unit.isFree && unit.enrollmentStatus !== 'not_enrolled'
            ? 'border-l-indigo-500'
            : unit.paymentStatus === 'pending'
              ? 'border-l-orange-500'
              : unit.enrollmentStatus === 'active' || unit.enrollmentStatus === 'completed'
              ? 'border-l-green-500'
              : 'border-l-gray-300'
          }`}
        onClick={handleCardClick}
      >
        {/* Activity Notifications */}
        {unit.hasLiveLesson && (
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 flex items-center justify-center gap-2 animate-pulse z-10 rounded-t-lg">
            <span className="text-sm font-black uppercase tracking-wide">🔴 LIVE LESSON NOW</span>
            {unit.liveLessonTitle && (
              <span className="text-xs font-medium opacity-90">- {unit.liveLessonTitle}</span>
            )}
          </div>
        )}

        {!unit.hasLiveLesson && unit.hasActiveVirtualClass && (
          <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 flex items-center justify-center gap-2 animate-pulse z-10 rounded-t-lg">
            <span className="text-sm font-black uppercase tracking-wide">📹 VIRTUAL CLASS ACTIVE</span>
          </div>
        )}

        {/* Self-Paced Badge */}
        {unit.selfPacedEnabled && (
          <div className={`absolute ${unit.hasLiveLesson || unit.hasActiveVirtualClass ? 'top-12' : 'top-0'} right-0 mt-2 mr-2 z-20`}>
            <span className="bg-purple-100 text-purple-800 text-xs font-semibold px-2.5 py-0.5 rounded border border-purple-200">
              Self-Paced
            </span>
          </div>
        )}

        <div className={`flex items-start justify-between mb-4 ${unit.hasLiveLesson || unit.hasActiveVirtualClass ? 'mt-14' : 'mt-2'}`}>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-lg">{getStatusIcon()}</span>
              <h3 className="text-lg font-semibold text-gray-900">
                {unit.name}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">{unit.code}</p>
            <p className="text-sm text-gray-700 line-clamp-2">{unit.description}</p>
          </div>
          <div className="flex flex-col items-end space-y-2">
            <span className={`status-badge ${statusColor}`}>
              {unit.selfPacedEnabled && statusText === 'Available to Enroll' ? 'Start Anytime' : statusText}
            </span>
            {unit.isRetake && (
              <span className="bg-orange-100 text-orange-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-orange-200 uppercase tracking-tighter">
                🔄 Retake
              </span>
            )}
          </div>
        </div>

        <div className="space-y-3 mb-4">
          {/* Lecturer/Tutor Information */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-start space-x-3">
              {(unit.Lecturer?.profilePicture || unit.lecturer?.profilePicture) ? (
                <img
                  src={getImageUrl(unit.Lecturer?.profilePicture || unit.lecturer?.profilePicture)}
                  alt={(unit.Lecturer || unit.lecturer)?.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center border-2 border-white shadow-sm">
                  <User className={`w-5 h-5 ${(unit.Lecturer || unit.lecturer) ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {unit.selfPacedEnabled ? (
                  <p className="text-sm text-gray-700 font-medium">
                    Tutored Course
                    <span className="block text-xs text-gray-500 font-normal">Expert tutors available</span>
                  </p>
                ) : (
                  (unit.Lecturer || unit.lecturer) ? (
                    <>
                      <p className="text-sm font-medium text-gray-900">{(unit.Lecturer || unit.lecturer).name}</p>
                      {(unit.Lecturer || unit.lecturer).campus?.name && (
                        <p className="text-xs text-gray-600">📍 {(unit.Lecturer || unit.lecturer).campus.name}</p>
                      )}
                      {unit.LecturerAssignment && (
                        <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                          {unit.LecturerAssignment.assignedAt && (
                            <p>📅 Assigned: {new Date(unit.LecturerAssignment.assignedAt).toLocaleDateString()}</p>
                          )}
                          {unit.LecturerAssignment.acceptedAt && (
                            <p>✅ Accepted: {new Date(unit.LecturerAssignment.acceptedAt).toLocaleDateString()}</p>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-orange-700 font-medium">
                      {unit.availabilityMessage || '👨‍🏫 Lecturer not assigned'}
                    </p>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Other Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {unit.selfPacedEnabled ? 'Flexible Schedule' : (unit.classDate ? new Date(unit.classDate).toLocaleDateString() : 'TBD')}
              </span>
            </div>
            <div className={`flex flex-col space-y-1 p-2 rounded-lg ${unit.isFree ? 'bg-indigo-50 border border-indigo-100' : ''}`}>
              <div className="flex items-center space-x-2">
                <DollarSign className={`w-4 h-4 ${unit.isFree ? 'text-indigo-600' : 'text-gray-500'}`} />
                <span className={`text-sm font-bold ${unit.isFree ? 'text-indigo-700' : 'text-gray-700'}`}>
                  {unit.isFree ? 'FREE' : (unit.cost ? `${unit.currency} ${unit.cost.toLocaleString()}` : 'TBD')}
                </span>
              </div>
              {unit.isFree && unit.freeDescription && (
                <p className="text-[10px] text-indigo-600 italic leading-tight line-clamp-2">
                  {unit.freeDescription}
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <BookOpen className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">
                {unit.enrollmentStatus === 'not_enrolled'
                  ? 'Not enrolled'
                  : unit.isFree
                    ? '✔ Free Access'
                    : unit.paymentStatus === 'confirmed'
                      ? 'Paid & Enrolled'
                      : unit.paymentStatus === 'pending'
                        ? 'Payment Pending'
                        : 'Enrolled'}
              </span>
            </div>
          </div>
        </div>

        {/* Progress Bar for active enrolled units */}
        {unit.enrollmentStatus === 'active' && unit.paymentStatus === 'confirmed' && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progress</span>
              <span>{unit.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${unit.progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Completed Unit Status */}
        {unit.enrollmentStatus === 'completed' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
            <p className="text-sm font-semibold text-blue-800">🎉 Unit Completed!</p>
            <p className="text-xs text-blue-600 mt-1">You have graduated from this unit.</p>
          </div>
        )}

        {/* Availability Message for Unavailable Units */}
        {unit.enrollmentStatus === 'not_enrolled' && !unit.isAvailableForEnrollment && unit.availabilityMessage && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800">
              ℹ️ {unit.availabilityMessage}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-2">
          {/* Completed Unit Actions */}
          {unit.enrollmentStatus === 'completed' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Are you sure you want to retake this unit? This will reset your progress and you will need to re-enroll and pay again.')) {
                  handleRetakeUnit(unit.studentUnitId, unit.id);
                }
              }}
              className="flex-1 bg-white border-2 border-primary-600 text-primary-600 px-4 py-2 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              🔄 Retake Unit
            </button>
          )}

          {unit.enrollmentStatus === 'not_enrolled' && unit.isAvailableForEnrollment && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEnrollUnit(unit.id, unit.name);
              }}
              className="btn-success flex-1 hover:bg-green-700"
            >
              <span className="mr-2">🎓</span>
              {unit.selfPacedEnabled ? 'Start Learning' : 'Enroll Now'}
            </button>
          )}
          {unit.enrollmentStatus !== 'not_enrolled' && unit.enrollmentStatus !== 'completed' && unit.paymentStatus === 'pending' && !unit.isFree && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/payment/${unit.id}`);
              }}
              className="btn-warning flex-1 hover:bg-orange-700"
            >
              <span className="mr-2">💳</span>
              Pay Now
            </button>
          )}
          {((unit.enrollmentStatus === 'active' || unit.enrollmentStatus === 'enrolled') && (unit.paymentStatus === 'confirmed' || unit.isFree)) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (unit.selfPacedEnabled) {
                  navigate(`/self-paced/unit/${unit.id}`);
                } else {
                  navigate(`/units/${unit.id}`, { state: { unit, fromCourse: selectedCourse, fromFilter: filter } });
                }
              }}
              className="btn-primary flex-1 hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              📚 {unit.selfPacedEnabled ? 'Continue Learning →' : 'Continue Learning'}
            </button>
          )}
          {(unit.enrollmentStatus === 'completed' || (unit.history && unit.history.length > 0)) && unit.paymentStatus === 'confirmed' && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadUnitTranscript(unit.id, unit.name);
              }}
              disabled={downloading}
              className={`btn-secondary flex-1 hover:bg-gray-700 ${unit.enrollmentStatus !== 'completed' ? 'border-dashed' : ''}`}
            >
              <span className="mr-2">📄</span>
              {downloading ? 'Downloading...' : unit.enrollmentStatus === 'completed' ? 'View Transcript' : 'Past Transcript'}
            </button>
          )}
          {unit.originalStatus === 'upcoming' && unit.enrollmentStatus !== 'not_enrolled' && (
            <button className="btn-secondary flex-1 hover:bg-gray-700">
              <span className="mr-2">📅</span>
              View Details
            </button>
          )}
          {unit.enrollmentStatus === 'not_enrolled' && !unit.isAvailableForEnrollment && (
            <button className="btn-secondary flex-1 opacity-50 cursor-not-allowed" disabled>
              <span className="mr-2">⏳</span>
              {unit.availabilityStatus === 'no_lecturer' ? 'No Lecturer' :
                unit.availabilityStatus === 'pending_acceptance' ? 'Pending Acceptance' :
                  unit.availabilityStatus === 'unit_inactive' ? 'Not Active' :
                    'Not Available'}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const CourseCard = ({ course }) => {
    const enrolledUnitsCount = units.filter(u => u.courseId === course.id && u.enrollmentStatus !== 'not_enrolled').length;
    const totalUnitsCount = course.Units?.length || 0;
    const progress = totalUnitsCount > 0 ? (enrolledUnitsCount / totalUnitsCount) * 100 : 0;

    return (
      <div
        onClick={() => setSelectedCourse(course)}
        className="card hover:shadow-large transition-all duration-300 cursor-pointer border-t-4 border-t-primary-500 bg-white"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-2">{course.name}</h3>
            <p className="text-sm text-gray-600 line-clamp-3 mb-4">{course.description}</p>
          </div>
          <BookOpen className="w-8 h-8 text-primary-200" />
        </div>

        <div className="space-y-4">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{enrolledUnitsCount} / {totalUnitsCount} Units Enrolled</span>
            <span className="font-medium text-primary-600">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>

          <button className="btn-primary w-full py-2 flex items-center justify-center">
            View Subjects
            <Clock className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Self-Paced Discovery Banner */}
      {isSelfPacedMode && !selectedCourse && (
        <div className="bg-gradient-to-r from-primary-600 to-indigo-700 rounded-xl p-6 text-white shadow-lg relative overflow-hidden animate-fade-in">
          <div className="absolute right-0 top-0 opacity-10 transform translate-x-1/4 -translate-y-1/4">
            <BookOpen className="w-64 h-64" />
          </div>
          <div className="relative z-10">
            <div className="flex items-center space-x-2 mb-2">
              <span className="px-2 py-0.5 bg-white bg-opacity-20 rounded-full text-xs font-bold uppercase tracking-wider border border-white border-opacity-30">
                Discovery Mode
              </span>
            </div>
            <h2 className="text-3xl font-bold mb-2">Self-Paced Learning Portal</h2>
            <p className="text-primary-100 max-w-2xl text-lg">
              Welcome to your flexible learning journey. You can browse all available courses, explore their subjects, and enroll in what interests you most—anytime, anywhere.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <div className="flex items-center space-x-2 bg-black bg-opacity-10 px-3 py-1.5 rounded-lg text-sm border border-white border-opacity-10">
                <CheckCircle className="w-4 h-4 text-green-300" />
                <span>Unlimited Access</span>
              </div>
              <div className="flex items-center space-x-2 bg-black bg-opacity-10 px-3 py-1.5 rounded-lg text-sm border border-white border-opacity-10">
                <Clock className="w-4 h-4 text-orange-300" />
                <span>Flexible Schedule</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center space-x-2">
            {selectedCourse && (
              <button
                onClick={() => setSelectedCourse(null)}
                className="p-1 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
              >
                <AlertCircle className="w-6 h-6 rotate-180" />
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              {selectedCourse ? selectedCourse.name : (isSelfPacedMode ? 'Browse Courses' : 'My Units')}
            </h1>
          </div>
          <p className="text-gray-600 mt-1">
            {selectedCourse
              ? `Exploring available units/subjects for ${selectedCourse.name}`
              : studentData?.course?.name
                ? `Course: ${studentData.course.name}`
                : isSelfPacedMode
                  ? 'Discover and enroll in available courses'
                  : 'Manage your enrolled courses and track progress'}
            {studentData?.campus?.name && !selectedCourse && ` • ${studentData.campus.name}`}
          </p>

          {/* Course Completion Status */}
          {studentData?.courseStatus && (
            <div className="mt-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${studentData.courseStatus === 'completed'
                ? 'bg-green-100 text-green-800'
                : studentData.courseStatus === 'in-progress'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
                }`}>
                {studentData.courseStatus === 'completed' && '🎉 '}
                {studentData.courseStatus === 'in-progress' && '📚 '}
                {studentData.courseStatus === 'enrolled' && '📝 '}
                Course Status: {
                  studentData.courseStatus === 'completed' ? 'Completed' :
                    studentData.courseStatus === 'in-progress' ? 'In Progress' :
                      'Enrolled'
                }
              </span>
            </div>
          )}
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
          <button
            onClick={handleDownloadTranscript}
            disabled={downloading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors flex items-center"
          >
            <FileText className="w-4 h-4 mr-2" />
            {downloading ? 'Processing...' : 'Download Transcript'}
          </button>
          {[
            { key: 'all', label: 'All Units', count: units.length },
            { key: 'active', label: 'Active', count: units.filter(u => u.status === 'active').length },
            { key: 'completed', label: 'Completed', count: units.filter(u => u.status === 'completed').length },
            { key: 'pending', label: 'Payment Pending', count: units.filter(u => u.paymentStatus === 'pending').length }
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === key
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {label} ({count})
            </button>
          ))}
        </div>
      </div>




      {/* Courses or Units Grid */}
      {isSelfPacedMode && !selectedCourse ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map(course => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      ) : filteredUnits.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredUnits.map((unit) => (
            <UnitCard key={unit.id} unit={unit} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No units found</h3>
          <p className="text-gray-600">
            {filter === 'all'
              ? "You haven't enrolled in any units yet."
              : `No units match the "${filter}" filter.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default Units;
